/**
 * MeetingNotesPage — 3-stegs inline wizard för mötesanteckningar.
 *
 * Mönster: card under header (som Verksamheter-sidan).
 * Stegen visas i samma inline-card med steg-indikatorer.
 * CreateMeetingNoteModal används inte längre.
 *
 * Roller: super_admin, customer_admin, employee
 */
import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { FileText, Plus, Trash2, Calendar as CalendarIcon, X, Paperclip } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────

export const MOTESTYPER = [
  'Introduktionsmöte',
  'Uppföljningsmöte',
  'Utvärderingsmöte',
  'Handledning',
  'Teammöte',
  'Kundmöte',
  'Individuellt stödsamtal',
];

const UPPFOLJNING_STATUSES = [
  { value: 'ej_paborjad', label: 'Ej påbörjad' },
  { value: 'pagaende',    label: 'Pågående' },
  { value: 'klar',        label: 'Klar' },
];

const STEP_LABELS = ['Grundinfo', 'Detaljer', 'Uppföljning'];
const TOTAL_STEPS = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_BILAGOR   = 5;
const WRITE_ROLES   = ['super_admin', 'customer_admin', 'employee'];

const ALLOWED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
];

const formatFileSize = (bytes) => {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const selectCls = (err) =>
  `w-full h-10 px-3 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
    err ? 'border-destructive focus:ring-destructive' : 'border-input'
  }`;

// ── ID helpers ─────────────────────────────────────────────────────────────────

let _dpCounter = 0;
let _ufCounter = 0;
const newDpId = () => `dp_${++_dpCounter}_${Date.now()}`;
const newUfId = () => `uf_${++_ufCounter}_${Date.now()}`;

// ── Empty form state ───────────────────────────────────────────────────────────

const buildEmpty = () => ({
  participant_id:      '',
  enhet:               '',
  motesdatum:          new Date().toISOString().split('T')[0],
  motestyp:            MOTESTYPER[0],
  rubrik:              '',
  anteckningar:        '',
  deltagande_personer: [],
  agendaText:          '',
  nasta_mote:          '',
  bilagor:             [],
  uppfoljningspunkter: [],
});

// ── MeetingNoteWizard ──────────────────────────────────────────────────────────

const MeetingNoteWizard = ({ participants, onSave, onCancel }) => {
  const { user }                    = useAuth();
  const [step, setStep]             = useState(1);
  const [form, setForm]             = useState(buildEmpty());
  const [errors, setErrors]         = useState({});
  const [submitting, setSub]        = useState(false);
  const [orgUsers, setOrgUsers]     = useState([]);

  useEffect(() => {
    api.get('/users').then(r => setOrgUsers(r.data || [])).catch(() => {});
  }, []);

  const setField = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const clearErr = (key) => setErrors(e => { const n = { ...e }; delete n[key]; return n; });

  // ── Validering ────────────────────────────────────────────────────────────

  const validateStep1 = (f) => {
    const e = {};
    if (!f.participant_id)        e.participant_id = 'Deltagare är obligatorisk';
    if (!f.motesdatum)            e.motesdatum     = 'Mötesdatum är obligatoriskt';
    if (!f.motestyp)              e.motestyp       = 'Mötestyp är obligatorisk';
    if (!f.anteckningar.trim())   e.anteckningar   = 'Anteckningar är obligatoriska';
    if (f.enhet.length > 120)     e.enhet          = 'Max 120 tecken';
    return e;
  };

  const validateStep2 = (f) => {
    const e = {};
    const dpErrs = {};
    f.deltagande_personer.forEach((dp, i) => {
      if (!dp.namn.trim()) dpErrs[i] = 'Namn krävs';
    });
    if (Object.keys(dpErrs).length) e.deltagande_personer = dpErrs;
    return e;
  };

  const validateStep3 = (f) => {
    const e = {};
    const ufErrs = {};
    f.uppfoljningspunkter.forEach((uf, i) => {
      const row = {};
      if (!uf.beskrivning.trim()) row.beskrivning = 'Beskrivning krävs';
      if (!uf.ansvarig)           row.ansvarig    = 'Ansvarig är obligatorisk';
      if (Object.keys(row).length) ufErrs[i] = row;
    });
    if (Object.keys(ufErrs).length) e.uppfoljningspunkter = ufErrs;
    return e;
  };

  const hasErrors = (e) =>
    Object.keys(e).some(k => {
      const v = e[k];
      return typeof v === 'object' && v !== null ? Object.keys(v).length > 0 : !!v;
    });

  // ── Stegnavigation ────────────────────────────────────────────────────────

  const handleNext = () => {
    const errs = step === 1 ? validateStep1(form)
               : step === 2 ? validateStep2(form)
               : {};
    setErrors(errs);
    if (hasErrors(errs)) return;
    setErrors({});
    setStep(s => s + 1);
  };

  const handleBack = () => { setErrors({}); setStep(s => s - 1); };

  // ── Deltagande personer ───────────────────────────────────────────────────

  const addPerson = () =>
    setForm(f => ({ ...f, deltagande_personer: [...f.deltagande_personer, { id: newDpId(), namn: '', roll: '' }] }));

  const updatePerson = (idx, field, value) => {
    setForm(f => ({
      ...f,
      deltagande_personer: f.deltagande_personer.map((dp, i) => i === idx ? { ...dp, [field]: value } : dp),
    }));
    if (field === 'namn') {
      setErrors(e => {
        const dp = { ...(e.deltagande_personer || {}) };
        if (value.trim()) delete dp[idx]; else dp[idx] = 'Namn krävs';
        return { ...e, deltagande_personer: Object.keys(dp).length ? dp : undefined };
      });
    }
  };

  const removePerson = (idx) => {
    setForm(f => ({ ...f, deltagande_personer: f.deltagande_personer.filter((_, i) => i !== idx) }));
    setErrors(e => {
      const dp = { ...(e.deltagande_personer || {}) };
      const ri = {};
      Object.entries(dp).forEach(([k]) => {
        const ki = Number(k);
        if (ki < idx) ri[ki] = dp[ki]; else if (ki > idx) ri[ki - 1] = dp[ki];
      });
      return { ...e, deltagande_personer: Object.keys(ri).length ? ri : undefined };
    });
  };

  // ── Bilagor ───────────────────────────────────────────────────────────────

  const handleFileAdd = (e) => {
    const files = Array.from(e.target.files || []);
    if (form.bilagor.length + files.length > MAX_BILAGOR) {
      toast.error(`Max ${MAX_BILAGOR} bilagor tillåtna`); e.target.value = ''; return;
    }
    const oversized = files.find(f => f.size > MAX_FILE_SIZE);
    const badType   = files.find(f => !ALLOWED_MIME.includes(f.type));
    if (oversized) { toast.error(`${oversized.name} är för stor (max 10 MB)`); e.target.value = ''; return; }
    if (badType)   { toast.error(`${badType.name} — filtypen stöds inte`);     e.target.value = ''; return; }
    setForm(f => ({ ...f, bilagor: [...f.bilagor, ...files] }));
    e.target.value = '';
  };

  const removeFile = (idx) =>
    setForm(f => ({ ...f, bilagor: f.bilagor.filter((_, i) => i !== idx) }));

  // ── Uppföljningspunkter ───────────────────────────────────────────────────

  const addUf = () =>
    setForm(f => ({
      ...f,
      uppfoljningspunkter: [
        ...f.uppfoljningspunkter,
        { id: newUfId(), beskrivning: '', ansvarig: '', deadline: '', status: 'ej_paborjad' },
      ],
    }));

  const updateUf = (idx, field, value) => {
    setForm(f => ({
      ...f,
      uppfoljningspunkter: f.uppfoljningspunkter.map((uf, i) => i === idx ? { ...uf, [field]: value } : uf),
    }));
    if (field === 'beskrivning' || field === 'ansvarig') {
      setErrors(e => {
        const ufErrs = { ...(e.uppfoljningspunkter || {}) };
        const row = { ...(ufErrs[idx] || {}) };
        if (field === 'beskrivning') { if (value.trim()) delete row.beskrivning; else row.beskrivning = 'Beskrivning krävs'; }
        if (field === 'ansvarig')    { if (value)        delete row.ansvarig;    else row.ansvarig    = 'Ansvarig är obligatorisk'; }
        if (Object.keys(row).length) ufErrs[idx] = row; else delete ufErrs[idx];
        return { ...e, uppfoljningspunkter: Object.keys(ufErrs).length ? ufErrs : undefined };
      });
    }
  };

  const removeUf = (idx) => {
    setForm(f => ({ ...f, uppfoljningspunkter: f.uppfoljningspunkter.filter((_, i) => i !== idx) }));
    setErrors(e => {
      const uf = { ...(e.uppfoljningspunkter || {}) };
      const ri = {};
      Object.entries(uf).forEach(([k]) => {
        const ki = Number(k);
        if (ki < idx) ri[ki] = uf[ki]; else if (ki > idx) ri[ki - 1] = uf[ki];
      });
      return { ...e, uppfoljningspunkter: Object.keys(ri).length ? ri : undefined };
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateStep3(form);
    setErrors(errs);
    if (hasErrors(errs)) return;

    let finalRubrik = form.rubrik.trim();
    if (!finalRubrik) {
      const p = participants.find(p => p.id === form.participant_id);
      finalRubrik = `${form.motestyp} – ${p?.name || 'Okänd'} – ${form.motesdatum}`;
    }

    setSub(true);
    try {
      const agendaItems = form.agendaText
        .split('\n').map(l => l.trim()).filter(Boolean).map(punkt => ({ punkt }));

      await api.post('/meeting-notes', {
        org_id:               user?.org_id || 'default',
        participant_id:       form.participant_id,
        motesdatum:           form.motesdatum,
        rubrik:               finalRubrik,
        motestyp:             form.motestyp,
        anteckningar:         form.anteckningar.trim(),
        enhet:                form.enhet.trim() || null,
        deltagande_personer:  form.deltagande_personer.map(({ namn, roll }) => ({ namn, roll })),
        agenda:               agendaItems,
        bilagor:              form.bilagor.map(f => f.name),
        nasta_mote:           form.nasta_mote || null,
        uppfoljningspunkter:  form.uppfoljningspunkter.map(
          ({ beskrivning, ansvarig, deadline, status }) =>
            ({ beskrivning, ansvarig: ansvarig || null, deadline: deadline || null, status })
        ),
      });
      toast.success('Mötesanteckning sparad!');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte spara mötesanteckning');
    } finally {
      setSub(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Card
      className="border-2 animate-in fade-in-0 slide-in-from-top-2 duration-200"
      style={{ borderColor: '#F9CF3F' }}
      data-testid="meeting-inline-form"
    >
      <CardContent className="pt-5 pb-6 px-6">

        {/* Steg-indikatorer */}
        <div className="flex items-center gap-3 mb-5">
          {STEP_LABELS.map((label, i) => {
            const s = i + 1;
            const active  = s === step;
            const done    = s < step;
            return (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2">
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                    style={{
                      background: active || done ? '#F9CF3F' : '#e5e7eb',
                      color: active || done ? '#333333' : '#9ca3af',
                    }}
                    data-testid={`step-indicator-${s}`}
                  >
                    {done ? '✓' : s}
                  </span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: active ? '#333333' : '#9ca3af' }}
                  >
                    {label}
                  </span>
                </div>
                {i < STEP_LABELS.length - 1 && (
                  <div
                    className="flex-1 h-px"
                    style={{ background: done ? '#F9CF3F' : '#e5e7eb' }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} noValidate data-testid="meeting-form">

          {/* ══ Steg 1 — Grundinfo ══ */}
          {step === 1 && (
            <div className="space-y-4" data-testid="step-1-content">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
                Grundinfo
              </p>

              {/* Deltagare */}
              <div className="space-y-1.5">
                <Label htmlFor="mn-participant">
                  Deltagare <span className="text-destructive" aria-hidden="true">*</span>
                </Label>
                <select
                  id="mn-participant"
                  data-testid="mn-participant-select"
                  value={form.participant_id}
                  onChange={e => { setField('participant_id', e.target.value); clearErr('participant_id'); }}
                  className={selectCls(errors.participant_id)}
                  aria-required="true"
                  aria-invalid={!!errors.participant_id}
                  autoFocus
                >
                  <option value="">Välj deltagare...</option>
                  {participants.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {errors.participant_id && (
                  <p role="alert" className="text-xs text-destructive">{errors.participant_id}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Mötesdatum */}
                <div className="space-y-1.5">
                  <Label htmlFor="mn-motesdatum">
                    Mötesdatum <span className="text-destructive" aria-hidden="true">*</span>
                  </Label>
                  <Input
                    id="mn-motesdatum"
                    data-testid="mn-motesdatum-input"
                    type="date"
                    value={form.motesdatum}
                    onChange={e => { setField('motesdatum', e.target.value); clearErr('motesdatum'); }}
                    aria-required="true"
                    aria-invalid={!!errors.motesdatum}
                    className={errors.motesdatum ? 'border-destructive focus-visible:ring-destructive' : ''}
                  />
                  {errors.motesdatum && (
                    <p role="alert" className="text-xs text-destructive">{errors.motesdatum}</p>
                  )}
                </div>

                {/* Mötestyp */}
                <div className="space-y-1.5">
                  <Label htmlFor="mn-motestyp">
                    Mötestyp <span className="text-destructive" aria-hidden="true">*</span>
                  </Label>
                  <select
                    id="mn-motestyp"
                    data-testid="mn-motestyp-select"
                    value={form.motestyp}
                    onChange={e => { setField('motestyp', e.target.value); clearErr('motestyp'); }}
                    className={selectCls(errors.motestyp)}
                    aria-required="true"
                    aria-invalid={!!errors.motestyp}
                  >
                    {MOTESTYPER.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {errors.motestyp && (
                    <p role="alert" className="text-xs text-destructive">{errors.motestyp}</p>
                  )}
                </div>
              </div>

              {/* Rubrik */}
              <div className="space-y-1.5">
                <Label htmlFor="mn-rubrik">Rubrik</Label>
                <Input
                  id="mn-rubrik"
                  data-testid="mn-rubrik-input"
                  placeholder="Lämna tomt för automatisk rubrik"
                  value={form.rubrik}
                  onChange={e => setField('rubrik', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Om tomt genereras: [Mötestyp] – [Deltagarens namn] – [Datum]
                </p>
              </div>

              {/* Anteckningar */}
              <div className="space-y-1.5">
                <Label htmlFor="mn-anteckningar">
                  Anteckningar <span className="text-destructive" aria-hidden="true">*</span>
                </Label>
                <Textarea
                  id="mn-anteckningar"
                  data-testid="mn-anteckningar-input"
                  placeholder="Skriv anteckningar från mötet..."
                  value={form.anteckningar}
                  onChange={e => { setField('anteckningar', e.target.value); clearErr('anteckningar'); }}
                  rows={4}
                  aria-required="true"
                  aria-invalid={!!errors.anteckningar}
                  className={errors.anteckningar ? 'border-destructive focus-visible:ring-destructive' : ''}
                />
                {errors.anteckningar && (
                  <p role="alert" className="text-xs text-destructive">{errors.anteckningar}</p>
                )}
              </div>

              {/* Enhet */}
              <div className="space-y-1.5">
                <Label htmlFor="mn-enhet">Enhet</Label>
                <Input
                  id="mn-enhet"
                  data-testid="mn-enhet-input"
                  placeholder="T.ex. avdelning eller enhet"
                  maxLength={120}
                  value={form.enhet}
                  onChange={e => { setField('enhet', e.target.value); clearErr('enhet'); }}
                  aria-invalid={!!errors.enhet}
                  className={errors.enhet ? 'border-destructive focus-visible:ring-destructive' : ''}
                />
                {errors.enhet && (
                  <p role="alert" className="text-xs text-destructive">{errors.enhet}</p>
                )}
              </div>
            </div>
          )}

          {/* ══ Steg 2 — Detaljer ══ */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2-content">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
                Detaljer
              </p>

              {/* Deltagande personer */}
              <div className="space-y-2">
                <Label>Deltagande personer</Label>
                <div className="space-y-2" data-testid="persons-list">
                  {form.deltagande_personer.map((dp, idx) => (
                    <div key={dp.id}>
                      <div className="flex items-center gap-2">
                        <Input
                          data-testid={`dp-namn-${idx}`}
                          placeholder="Namn"
                          value={dp.namn}
                          onChange={e => updatePerson(idx, 'namn', e.target.value)}
                          aria-label={`Namn på deltagare ${idx + 1}`}
                          aria-invalid={!!(errors.deltagande_personer?.[idx])}
                          className={`flex-1 ${errors.deltagande_personer?.[idx] ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                        />
                        <Input
                          data-testid={`dp-roll-${idx}`}
                          placeholder="Roll (valfritt)"
                          value={dp.roll}
                          onChange={e => updatePerson(idx, 'roll', e.target.value)}
                          aria-label={`Roll för deltagare ${idx + 1}`}
                          className="flex-1"
                        />
                        <button
                          type="button"
                          data-testid={`remove-dp-${idx}`}
                          onClick={() => removePerson(idx)}
                          aria-label={`Ta bort deltagare ${idx + 1}`}
                          className="shrink-0 p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      {errors.deltagande_personer?.[idx] && (
                        <p role="alert" className="mt-1 text-xs text-destructive">
                          {errors.deltagande_personer[idx]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="add-person-btn"
                  onClick={addPerson}
                  className="flex items-center gap-1.5 text-sm font-medium hover:opacity-70 transition-opacity"
                >
                  <Plus size={15} style={{ color: '#F9CF3F' }} />
                  <span style={{ color: '#333333' }}>Lägg till person</span>
                </button>
              </div>

              {/* Agenda */}
              <div className="space-y-1.5">
                <Label htmlFor="mn-agenda">Agenda</Label>
                <Textarea
                  id="mn-agenda"
                  data-testid="mn-agenda-input"
                  placeholder="En agendapunkt per rad"
                  value={form.agendaText}
                  onChange={e => setField('agendaText', e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">En agendapunkt per rad.</p>
              </div>

              {/* Nästa möte */}
              <div className="space-y-1.5">
                <Label htmlFor="mn-nasta-mote">Nästa möte</Label>
                <Input
                  id="mn-nasta-mote"
                  data-testid="mn-nasta-mote-input"
                  type="date"
                  value={form.nasta_mote}
                  onChange={e => setField('nasta_mote', e.target.value)}
                />
              </div>

              {/* Bilagor */}
              <div className="space-y-2">
                <Label>
                  Bilagor
                  <span className="ml-1 text-xs text-muted-foreground">(max {MAX_BILAGOR})</span>
                </Label>
                <div className="space-y-1" data-testid="bilagor-list">
                  {form.bilagor.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <Paperclip size={14} className="text-muted-foreground shrink-0" />
                      <span className="flex-1 truncate" data-testid={`bilaga-name-${idx}`}>{file.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0" data-testid={`bilaga-size-${idx}`}>
                        {formatFileSize(file.size)}
                      </span>
                      <button
                        type="button"
                        data-testid={`remove-bilaga-${idx}`}
                        onClick={() => removeFile(idx)}
                        aria-label={`Ta bort bilaga ${file.name}`}
                        className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                {form.bilagor.length < MAX_BILAGOR && (
                  <label
                    htmlFor="mn-file-input"
                    className="inline-flex items-center gap-1.5 cursor-pointer text-sm font-medium hover:opacity-70 transition-opacity"
                  >
                    <Paperclip size={15} style={{ color: '#F9CF3F' }} />
                    <span style={{ color: '#333333' }}>Lägg till bilaga</span>
                    <input
                      id="mn-file-input"
                      data-testid="mn-file-input"
                      type="file"
                      className="sr-only"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                      onChange={handleFileAdd}
                    />
                  </label>
                )}
                <p className="text-xs text-muted-foreground">PDF, Word, Excel eller bild. Max 10 MB per fil.</p>
              </div>
            </div>
          )}

          {/* ══ Steg 3 — Uppföljning ══ */}
          {step === 3 && (
            <div data-testid="step-3-content">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
                Uppföljningspunkter
              </p>
              <div className="space-y-3" data-testid="uf-list">
                {form.uppfoljningspunkter.map((uf, idx) => (
                  <div key={uf.id} data-testid={`uf-row-${idx}`} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 space-y-1.5">
                        <Label htmlFor={`uf-beskrivning-${idx}`}>
                          Beskrivning <span className="text-destructive" aria-hidden="true">*</span>
                        </Label>
                        <Input
                          id={`uf-beskrivning-${idx}`}
                          data-testid={`uf-beskrivning-${idx}`}
                          placeholder="Vad ska följas upp?"
                          value={uf.beskrivning}
                          onChange={e => updateUf(idx, 'beskrivning', e.target.value)}
                          aria-invalid={!!(errors.uppfoljningspunkter?.[idx]?.beskrivning)}
                          className={errors.uppfoljningspunkter?.[idx]?.beskrivning ? 'border-destructive focus-visible:ring-destructive' : ''}
                        />
                        {errors.uppfoljningspunkter?.[idx]?.beskrivning && (
                          <p role="alert" className="text-xs text-destructive">
                            {errors.uppfoljningspunkter[idx].beskrivning}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        data-testid={`remove-uf-${idx}`}
                        onClick={() => removeUf(idx)}
                        aria-label={`Ta bort uppföljningspunkt ${idx + 1}`}
                        className="mt-6 shrink-0 p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor={`uf-ansvarig-${idx}`}>
                          Ansvarig <span className="text-destructive" aria-hidden="true">*</span>
                        </Label>
                        <select
                          id={`uf-ansvarig-${idx}`}
                          data-testid={`uf-ansvarig-${idx}`}
                          value={uf.ansvarig}
                          onChange={e => updateUf(idx, 'ansvarig', e.target.value)}
                          aria-invalid={!!(errors.uppfoljningspunkter?.[idx]?.ansvarig)}
                          className={selectCls(errors.uppfoljningspunkter?.[idx]?.ansvarig)}
                        >
                          <option value="">Välj ansvarig</option>
                          {orgUsers.map(u => (
                            <option key={u.id} value={u.id}>{u.name || u.email}</option>
                          ))}
                        </select>
                        {errors.uppfoljningspunkter?.[idx]?.ansvarig && (
                          <p role="alert" className="text-xs text-destructive">
                            {errors.uppfoljningspunkter[idx].ansvarig}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`uf-deadline-${idx}`}>Deadline</Label>
                        <Input
                          id={`uf-deadline-${idx}`}
                          data-testid={`uf-deadline-${idx}`}
                          type="date"
                          value={uf.deadline}
                          onChange={e => updateUf(idx, 'deadline', e.target.value)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`uf-status-${idx}`}>Status</Label>
                        <select
                          id={`uf-status-${idx}`}
                          data-testid={`uf-status-${idx}`}
                          value={uf.status}
                          onChange={e => updateUf(idx, 'status', e.target.value)}
                          className={selectCls(false)}
                        >
                          {UPPFOLJNING_STATUSES.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                data-testid="add-uf-btn"
                onClick={addUf}
                className="mt-3 flex items-center gap-1.5 text-sm font-medium hover:opacity-70 transition-opacity"
              >
                <Plus size={15} style={{ color: '#F9CF3F' }} />
                <span style={{ color: '#333333' }}>Lägg till uppföljningspunkt</span>
              </button>
            </div>
          )}

          {/* ══ Navigeringsknappar ══ */}
          <div className="flex items-center justify-between gap-3 pt-5 border-t mt-6">
            <div>
              {step > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleBack}
                  disabled={submitting}
                  data-testid="back-btn"
                  className="rounded-full"
                >
                  Föregående
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onCancel}
                  disabled={submitting}
                  className="rounded-full"
                >
                  Avbryt
                </Button>
              )}
            </div>
            <div>
              {step < TOTAL_STEPS ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  data-testid="next-btn"
                  className="rounded-full font-bold px-8"
                  style={{ background: '#F9CF3F', color: '#333333' }}
                >
                  Nästa
                </Button>
              ) : (
                <Button
                  type="submit"
                  data-testid="submit-btn"
                  disabled={submitting}
                  className="rounded-full font-bold px-8"
                  style={{ background: '#F9CF3F', color: '#333333' }}
                >
                  {submitting ? 'Sparar...' : 'Spara anteckning'}
                </Button>
              )}
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ── MeetingNotesPage ───────────────────────────────────────────────────────────

const MeetingNotesPage = () => {
  const { user }                        = useAuth();
  const [notes, setNotes]               = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [formOpen, setFormOpen]         = useState(false);

  const canWrite = WRITE_ROLES.includes(user?.role);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [notesRes, participantsRes] = await Promise.all([
        api.get('/meeting-notes'),
        api.get('/participants'),
      ]);
      setNotes(notesRes.data);
      setParticipants(participantsRes.data);
    } catch {
      toast.error('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Är du säker på att du vill radera denna mötesanteckning?')) return;
    try {
      await api.delete(`/meeting-notes/${noteId}`);
      toast.success('Mötesanteckning raderad!');
      fetchData();
    } catch {
      toast.error('Kunde inte radera mötesanteckning');
    }
  };

  const getParticipantName = (pid) =>
    participants.find(p => p.id === pid)?.name || 'Okänd';

  const groupedByParticipant = notes.reduce((acc, note) => {
    const pid = note.participant_id;
    if (!acc[pid]) acc[pid] = [];
    acc[pid].push(note);
    return acc;
  }, {});

  const handleSaved = async () => {
    setFormOpen(false);
    await fetchData();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="meeting-notes-page">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Mötesanteckningar</h1>
            <p className="text-muted-foreground mt-1">Mötesdokumentation och uppföljning</p>
          </div>
          {canWrite && (
            formOpen ? (
              <Button variant="ghost" className="rounded-full" onClick={() => setFormOpen(false)}>
                <X size={16} className="mr-1.5" /> Avbryt
              </Button>
            ) : (
              <Button
                data-testid="create-meeting-note-btn"
                className="rounded-full font-bold"
                onClick={() => setFormOpen(true)}
              >
                <Plus size={18} className="mr-2" /> Ny mötesanteckning
              </Button>
            )
          )}
        </div>

        {/* ── Inline wizard ── */}
        {canWrite && formOpen && (
          <MeetingNoteWizard
            participants={participants}
            onSave={handleSaved}
            onCancel={() => setFormOpen(false)}
          />
        )}

        {/* ── Statistik ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: 'Totalt möten',  value: notes.length,                                                          icon: FileText,    color: 'text-blue-600' },
            { label: 'Deltagare',     value: Object.keys(groupedByParticipant).length,                              icon: CalendarIcon, color: 'text-green-600' },
            { label: 'Uppföljningar', value: notes.reduce((s, n) => s + (n.uppfoljningspunkter?.length || 0), 0),  icon: FileText,    color: 'text-primary' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold mt-1">{value}</p>
                </div>
                <Icon size={28} className={color} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Anteckningslista ── */}
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center text-muted-foreground">
              Laddar mötesanteckningar...
            </CardContent>
          </Card>
        ) : notes.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText size={48} className="mx-auto mb-4 text-muted-foreground/20" />
              <p className="text-muted-foreground text-lg">Inga mötesanteckningar ännu.</p>
              {canWrite && !formOpen && (
                <Button
                  className="mt-4 rounded-full font-bold"
                  onClick={() => setFormOpen(true)}
                >
                  <Plus size={16} className="mr-2" /> Skapa första anteckningen
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedByParticipant).map(([participantId, participantNotes]) => (
            <div key={participantId} className="space-y-4">
              <h2 className="text-xl font-heading font-bold">{getParticipantName(participantId)}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {participantNotes.map(note => (
                  <Card key={note.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-5 pb-4 px-5">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-bold text-base leading-tight truncate">{note.rubrik}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {formatDateTime(note.motesdatum)} · {note.motestyp}
                          </p>
                        </div>
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="shrink-0 -mt-1"
                            onClick={() => handleDelete(note.id)}
                          >
                            <Trash2 size={14} className="text-destructive" />
                          </Button>
                        )}
                      </div>
                      {note.anteckningar && (
                        <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                          {note.anteckningar}
                        </p>
                      )}
                      {note.uppfoljningspunkter?.length > 0 && (
                        <p className="text-xs font-semibold text-muted-foreground border-t pt-2 mt-2">
                          {note.uppfoljningspunkter.length} uppföljningspunkt{note.uppfoljningspunkter.length !== 1 ? 'er' : ''}
                        </p>
                      )}
                      {note.nasta_mote && (
                        <div className="bg-blue-50 px-3 py-2 rounded-lg mt-2">
                          <p className="text-xs font-bold text-blue-600">
                            Nästa möte: {formatDateTime(note.nasta_mote)}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
};

export { MeetingNoteWizard };
export default MeetingNotesPage;
